"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlgorithmResult = exports.StopCriterion = exports.WesolowskyPolynomialApproximator = void 0;
var LowerUpperMatrixDecomposition_1 = require("./LowerUpperMatrixDecomposition");
/// <summary>
/// Класс для аппроксимации полиномами с использованием алгоритма Весоловского.
/// Суть данного алгоритма это метод для задачи наименьших абсолютных отклонений (LAD-регрессии, минимизация L1-нормы).
/// Алгоритм начинает с некоторого приближения (например, МНК)
/// и находит ближайшее к нему "ребро" (точку данных, где невязка минимальна).
/// Затем он "скользит" по этому ребру, находя точку на нём, где функция F минимальна.
/// </summary>
var WesolowskyPolynomialApproximator = /** @class */ (function () {
    /// <summary>
    /// Конструктор класса для аппроксимации полиномами
    /// </summary>
    /// <param name="bufferSize"> Размер массивов для хранения исходных точек </param>
    /// <param name="degree"> Степень полинома </param>
    function WesolowskyPolynomialApproximator(bufferSize, degree) {
        /// <summary>
        /// Реальная длина массивов исходных значений
        /// </summary>
        this._actualLength = 0;
        this._bufferSize = bufferSize;
        this._degree = degree;
        // Выделяем память для буферов
        this._xBuffer = new Float64Array(bufferSize);
        this._yBuffer = new Float64Array(bufferSize);
        this._residuals = new Float64Array(bufferSize);
        this._keys = new Float64Array(bufferSize);
        this._values = new Float64Array(bufferSize);
        this._currentValues = new Float64Array(bufferSize);
        this._basisValues = new Float64Array(bufferSize);
        this._indicesSort = new Int32Array(bufferSize);
        this._multiplyResult = new Float64Array(degree + 1);
        this._basisCoeffs = new Float64Array(degree + 1);
        this._currentCoeffs = new Float64Array(degree + 1);
        this._bestX = new Float64Array(degree + 1);
        this._bestIndices = new Int32Array(degree + 1);
        this._previousBestIndices = new Int32Array(degree + 1);
        // Список для хранения невязок
        this._residualHistory = [];
        // память для 1 итерации
        // память для построения интерполированного
        // полинома с использованием LUP
        this._interpolationA = new Float64Array((degree + 1) * (degree + 1));
        this._interpolationb = new Float64Array(degree + 1);
        this._dstA = new Float64Array((degree + 1) * (degree + 1));
        this._permutation = new Int32Array(degree + 1);
        this._solutionBuffer = new Float64Array(degree + 1);
        // результат алгоритма
        this._algorithmResult = new AlgorithmResult();
    }
    Object.defineProperty(WesolowskyPolynomialApproximator.prototype, "degree", {
        /// <summary>
        /// Доступ к степени полинома
        /// </summary>
        get: function () {
            return this._degree;
        },
        enumerable: false,
        configurable: true
    });
    /// <summary>
    /// Запуск алгоритма аппроксимации полиномом
    /// </summary>
    /// <param name="y"> Массив исходных точек Y </param>
    /// <param name="maxIterations"> Максимальное число итераций </param>
    /// <param name="residualThreshold"> Порог невязки для остановки </param>
    /// <returns>
    /// Результат алгоритма с критерием остановки
    /// </returns>
    WesolowskyPolynomialApproximator.prototype.approximate = function (y, maxIterations, residualThreshold) {
        if (maxIterations === void 0) { maxIterations = 100; }
        if (residualThreshold === void 0) { residualThreshold = 0; }
        // Валидация
        if (y === null)
            throw new Error('ArgumentNullException');
        if (this._actualLength !== y.length)
            throw new Error('ArgumentException');
        if (this._degree < 1)
            throw new Error('ArgumentException');
        if (this._actualLength > this._bufferSize || y.length > this._bufferSize)
            throw new Error('ArgumentException');
        // Количество точек для интерполяции (на 1 больше степени полинома)
        var k = this._degree + 1;
        // Общее количество точек
        var n = this._actualLength;
        //Array.Copy(x, 0, _xBuffer, 0, n);
        this._yBuffer.set(y.slice(0, n), 0);
        // Инициализация
        var currentResidual = 0;
        var stopReason = StopCriterion.MaxIterations;
        var iteration;
        for (var i = 0; i < k; i++)
            this._bestIndices[i] = Math.floor((i * (this._actualLength - 1)) / (k - 1));
        // 1. Вычисляем коэффициенты P1:
        WesolowskyPolynomialApproximator.buildInterpolationPolynomial(this._bestIndices, this._xBuffer, this._yBuffer, this._interpolationA, this._interpolationb, this._dstA, this._permutation, this._solutionBuffer, this._currentCoeffs, k);
        // 2. Вычисление невязок
        this._currentValues = WesolowskyPolynomialApproximator.evaluatePolynomials(this._xBuffer, this._currentCoeffs, this._currentValues, n); // Строим полином P1
        WesolowskyPolynomialApproximator.calculateResiduals(this._yBuffer, this._currentValues, this._residuals); // Вычисление невязок
        currentResidual = WesolowskyPolynomialApproximator.calculateMae(this._yBuffer, this._currentValues, n);
        this._residualHistory.push(currentResidual);
        // 3. Основной итерационный цикл
        for (iteration = 1; iteration < maxIterations; iteration++) {
            // 4. Сохраняем для следующей итерации
            this._previousBestIndices.set(this._bestIndices.slice(0, k), 0);
            for (var j = 0; j < k; j++) {
                // 5. Определяем базисный полином
                var changingPointIndex = (iteration - 1) % k;
                this._basisCoeffs = WesolowskyPolynomialApproximator.buildBasisPolynomial(this._bestIndices, this._xBuffer, changingPointIndex, this._basisCoeffs, this._multiplyResult, this._bestX);
                this._basisValues = WesolowskyPolynomialApproximator.evaluatePolynomials(this._xBuffer, this._basisCoeffs, this._basisValues, n);
                // 6. Изменение одной точки
                // C# `ref int` adapted to object wrapper for pass-by-reference
                var newPointIndexRef = { value: this._bestIndices[changingPointIndex] };
                WesolowskyPolynomialApproximator.calculateOptimalAlpha(y, this._currentValues, this._basisValues, this._keys, this._values, this._indicesSort, newPointIndexRef);
                this._bestIndices[changingPointIndex] = newPointIndexRef.value;
                WesolowskyPolynomialApproximator.buildInterpolationPolynomial(this._bestIndices, this._xBuffer, this._yBuffer, this._interpolationA, this._interpolationb, this._dstA, this._permutation, this._solutionBuffer, this._currentCoeffs, k);
                this._currentValues = WesolowskyPolynomialApproximator.evaluatePolynomials(this._xBuffer, this._currentCoeffs, this._currentValues, n);
            }
            // 7. Вычисление невязок
            WesolowskyPolynomialApproximator.calculateResiduals(this._yBuffer, this._currentValues, this._residuals);
            currentResidual = WesolowskyPolynomialApproximator.calculateMae(this._yBuffer, this._currentValues, n);
            this._residualHistory.push(currentResidual);
            // Проверка критерия 1: Выход в случае достижения максимального числа итераций
            if (iteration === maxIterations) {
                stopReason = StopCriterion.MaxIterations;
                break;
            }
            // Проверка критерия 2: Достижения порога невязки
            if (iteration > 2 && Math.abs(currentResidual - this._residualHistory[iteration - 2]) < residualThreshold) {
                stopReason = StopCriterion.ResidualThreshold;
                break;
            }
            // Проверка критерия 3: Изменение точек
            if (iteration > 1 && !WesolowskyPolynomialApproximator.tryPointChange(this._previousBestIndices, this._bestIndices)) {
                stopReason = StopCriterion.OptimumReach;
                break;
            }
        }
        // Формируем результат
        this._algorithmResult.finalCoefficients = this._currentCoeffs;
        this._algorithmResult.finalResidualThreshold = Math.abs(currentResidual - this._residualHistory[iteration - 2]);
        this._algorithmResult.finalResidual = currentResidual;
        this._algorithmResult.stopReason = stopReason;
        this._algorithmResult.iterationsPerformed = iteration;
        return this._algorithmResult;
    };
    /// <summary>
    /// Задать сетку узлов.
    /// </summary>
    /// <param name="x"> Узловые точки по оси времени. </param>
    WesolowskyPolynomialApproximator.prototype.setGrid = function (x) {
        if (x === null)
            throw new Error('ArgumentNullException');
        if (this._degree < 1)
            throw new Error('ArgumentException');
        this._actualLength = x.length;
        this._xBuffer.set(x, 0);
    };
    /// <summary>
    /// Проверка изменения точек
    /// </summary>
    /// <param name="prevIndices"> Массив индексов предыдущей итерации </param>
    /// <param name="currIndices"> Массив индексов текущей итерации </param>
    /// <returns>
    /// Изменение точек
    /// </returns>
    WesolowskyPolynomialApproximator.tryPointChange = function (prevIndices, currIndices) {
        if (prevIndices === null)
            return true;
        for (var i = 0; i < currIndices.length; i++) {
            if (prevIndices[i] !== currIndices[i])
                return true;
        }
        return false;
    };
    /// <summary>
    /// Построение базисного полинома
    /// </summary>
    /// <param name="pointsX"> Массив точек по X </param>
    /// <param name="selectedIndex"> Массив выбранных индексов </param>
    /// <param name="basisCoeffsBuffer"> Буфер коэффициентов базисного полинома </param>
    /// <param name="multiplyResult"> Результат умножения </param>
    /// <returns>
    /// Коэффициенты базисного полинома
    /// </returns>
    WesolowskyPolynomialApproximator._buildBasisPolynomial = function (pointsX, selectedIndex, basisCoeffsBuffer, multiplyResult) {
        var n = pointsX.length;
        // Инициализация базисного полинома: [1]
        basisCoeffsBuffer[0] = 1.0;
        var currentDegree = 0;
        // Умножаем на (x - zeroPoint) для каждой точки, кроме selectedIndex
        for (var i = 0; i < n; i++) {
            if (i === selectedIndex)
                continue;
            var zeroPoint = pointsX[i];
            // Очищаем multiplyResult
            multiplyResult.fill(0, 0, currentDegree + 2);
            // Умножение полиномов
            for (var j = 0; j <= currentDegree; j++) {
                if (basisCoeffsBuffer[j] !== 0) {
                    // Умножение на -zeroPoint (свободный член)
                    multiplyResult[j] += basisCoeffsBuffer[j] * (-zeroPoint);
                    // Умножение на x (коэффициент 1)
                    multiplyResult[j + 1] += basisCoeffsBuffer[j];
                }
            }
            // Копируем результат обратно в basisCoeffsBuffer
            currentDegree++;
            basisCoeffsBuffer.set(multiplyResult.slice(0, currentDegree + 1), 0);
        }
        // Нормировка
        var selectedX = pointsX[selectedIndex];
        var normValue = WesolowskyPolynomialApproximator.evaluatePolynomial(selectedX, multiplyResult);
        if (Math.abs(normValue) > 1e-15) {
            var invNorm = 1.0 / normValue;
            for (var i = 0; i <= currentDegree; i++)
                basisCoeffsBuffer[i] *= invNorm;
        }
        else {
            // Обнуляем остаток буфера (если степень меньше максимальной)
            for (var i = currentDegree + 1; i < basisCoeffsBuffer.length; i++)
                basisCoeffsBuffer[i] = 0;
        }
        return basisCoeffsBuffer;
    };
    /// <summary>
    /// Построение базисного полинома
    /// </summary>
    /// <param name="bestIndices"> Массив индексов лучших точек для замены </param>
    /// <param name="x"> Массив точек по X </param>
    /// <param name="selectedIndex"> Массив выбранных индексов </param>
    /// <param name="basisCoeffsBuffer"> Буфер коэффициентов базисного полинома </param>
    /// <param name="multiplyResult"> Результат умножения </param>
    /// <returns>
    /// Коэффициенты базисного полинома
    /// </returns>
    WesolowskyPolynomialApproximator.buildBasisPolynomial = function (bestIndices, x, selectedIndex, basisCoeffsBuffer, multiplyResult, pointsXBuffer) {
        for (var i = 0; i < bestIndices.length; i++) {
            pointsXBuffer[i] = x[bestIndices[i]];
        }
        WesolowskyPolynomialApproximator._buildBasisPolynomial(pointsXBuffer, selectedIndex, basisCoeffsBuffer, multiplyResult);
        return basisCoeffsBuffer;
    };
    /// <summary>
    /// Вычислить коэффициент аппроксимации alpha.
    /// </summary>
    /// <param name="y"> Исходные точки Y </param>
    /// <param name="currentValues"> Текущие коэффициенты полинома </param>
    /// <param name="basisValues"> Базисные коэффициенты полинома </param>
    /// <param name="keys"> Массив ключей для сортировки </param>
    /// <param name="values"> Массив значений для сортировки </param>
    /// <returns>
    /// Коэффициент аппроксимации alpha.
    /// </returns>
    WesolowskyPolynomialApproximator.calculateOptimalAlpha = function (y, currentValues, basisValues, keys, values, indicesSort, newPointIndexRef) {
        var sum = 0;
        for (var i = 0; i < y.length; i++) {
            var residual = y[i] - currentValues[i];
            var v = basisValues[i];
            keys[i] = residual / v;
            values[i] = Math.abs(v);
            sum += Math.abs(v);
            indicesSort[i] = i;
        }
        // C# Array.Sort(keys, indicesSort) — paired sort, sort indicesSort by keys values
        var paired = new Array(y.length).fill(0).map(function (_, i) { return ({ key: keys[i], idx: indicesSort[i] }); });
        paired.sort(function (a, b) { return ((a.key > b.key || isNaN(b.key)) ? 1 : -1); }); //manual fix: в массиве keys могут быть значения NaN из-за деления выше
        for (var i = 0; i < paired.length; i++) {
            keys[i] = paired[i].key;
            indicesSort[i] = paired[i].idx;
        }
        // Находим оптимальную alpha:
        var alpha = 0;
        var thr = sum / 2;
        sum = 0;
        for (var i = 0; i < y.length; i++) {
            var v = values[indicesSort[i]];
            sum += v;
            if (sum < thr)
                continue;
            alpha = keys[i];
            newPointIndexRef.value = indicesSort[i];
            break;
        }
        return alpha;
    };
    /// <summary>
    /// Вычисление полинома в точках
    /// </summary>
    /// <param name="x"> Исходные точки X </param>
    /// <param name="coefficients"> Массив коэффициентов полинома </param>
    /// <param name="result"> Результат вычислений </param>
    /// <param name="n"> Реальная длина массивов исходных точек </param>
    /// <returns>
    /// Значение полинома в точках
    /// </returns>
    WesolowskyPolynomialApproximator.evaluatePolynomials = function (x, coefficients, result, n) {
        for (var i = 0; i < n; i++)
            result[i] = WesolowskyPolynomialApproximator.evaluatePolynomial(x[i], coefficients);
        return result;
    };
    /// <summary>
    /// Вычисление полинома в точке
    /// </summary>
    /// <param name="x"> Исходные точки X </param>
    /// <param name="coefficients"> Массив коэффициентов полинома </param>
    /// <returns>
    /// Значение полинома в точке
    /// </returns>
    WesolowskyPolynomialApproximator.evaluatePolynomial = function (x, coefficients) {
        // Схема Горнера для эффективного вычисления
        var result = coefficients[coefficients.length - 1];
        for (var i = coefficients.length - 2; i >= 0; i--)
            result = result * x + coefficients[i];
        return result;
    };
    /// <summary>
    /// Построение интерполированного полинома
    /// </summary>
    /// <param name="bestIndices"> Массив индексов лучших точек для замены </param>
    /// <param name="x"> Исходные точки X </param>
    /// <param name="y"> Исходные точки Y </param>
    /// <param name="A"> Массив для матрицы Вандермонта </param>
    /// <param name="b"> Вектор b для хранения значений Y </param>
    /// <param name="dstA"> Массив для декомпозированной матрицы А </param>
    /// <param name="permutation"> Массив для вектора перестановок </param>
    /// <param name="solutionBuffer"> Буфер для хранения промежуточных решений </param>
    /// <param name="xSolve"> Решение системы </param>
    /// <param name="n"> Реальная длина массивов исходных точек </param>
    WesolowskyPolynomialApproximator.buildInterpolationPolynomial = function (bestIndices, x, y, A, b, dstA, permutation, solutionBuffer, xSolve, n) {
        for (var i = 0; i < n; i++) {
            b[i] = y[bestIndices[i]];
            var xi = x[bestIndices[i]];
            var power = 1;
            for (var j = 0; j < n; j++) {
                A[i * n + j] = power;
                power *= xi;
            }
        }
        // Добавляем LUP библиотеку
        LowerUpperMatrixDecomposition_1.LowerUpperMatrixDecomposition.tryDecompose(A, n, permutation, dstA);
        LowerUpperMatrixDecomposition_1.LowerUpperMatrixDecomposition.trySolve(dstA, 0, n, 1, n, b, 0, 1, xSolve, permutation, solutionBuffer);
    };
    /// <summary>
    /// Вычисление невязок
    /// </summary>
    /// <param name="original"> Исходный массив точек </param>
    /// <param name="approximated"> Массив аппроксимированных точек </param>
    /// <param name="residuals"> Массив невязок </param>
    WesolowskyPolynomialApproximator.calculateResiduals = function (original, approximated, residuals) {
        for (var i = 0; i < original.length; i++)
            residuals[i] = Math.abs(original[i] - approximated[i]);
    };
    /// <summary>
    /// Вычисление абсолютного среднего отклонения
    /// </summary>
    /// <param name="trueValues"> Реальные значения </param>
    /// <param name="predictedValues"> Аппроксимированные значения </param>
    /// <param name="n"> Реальная длина массивов исходных точек </param>
    /// <returns>
    /// Абсолютное среднее отклонение
    /// </returns>
    WesolowskyPolynomialApproximator.calculateMae = function (trueValues, predictedValues, n) {
        var sum = 0;
        for (var i = 0; i < n; i++) {
            sum += Math.abs(trueValues[i] - predictedValues[i]);
        }
        return sum / n;
    };
    return WesolowskyPolynomialApproximator;
}());
exports.WesolowskyPolynomialApproximator = WesolowskyPolynomialApproximator;
// ═══════════════════════════════════════════════════════════
// Enum: StopCriterion
// ═══════════════════════════════════════════════════════════
/// <summary>
/// Критерии остановки алгоритма
/// </summary>
var StopCriterion;
(function (StopCriterion) {
    /// <summary>
    /// 1. Изменение невязки меньше заданной точности
    /// </summary>
    StopCriterion[StopCriterion["ResidualThreshold"] = 0] = "ResidualThreshold";
    /// <summary>
    /// 2. Достижение оптимального значения
    /// </summary>
    StopCriterion[StopCriterion["OptimumReach"] = 1] = "OptimumReach";
    /// <summary>
    /// 3. Достигнуто максимальное число итераций
    /// </summary>
    StopCriterion[StopCriterion["MaxIterations"] = 2] = "MaxIterations";
})(StopCriterion || (exports.StopCriterion = StopCriterion = {}));
// ═══════════════════════════════════════════════════════════
// Class: AlgorithmResult
// ═══════════════════════════════════════════════════════════
/// <summary>
/// Результат работы алгоритма
/// </summary>
var AlgorithmResult = /** @class */ (function () {
    function AlgorithmResult() {
        /// <summary>
        /// Финальные коэффициенты полинома
        /// </summary>
        this.finalCoefficients = null;
        /// <summary>
        /// Финальное изменение невязки
        /// </summary>
        this.finalResidualThreshold = 0;
        /// <summary>
        /// Финальная невязка
        /// </summary>
        this.finalResidual = 0;
        /// <summary>
        /// Критерий остановки алгоритма
        /// </summary>
        this.stopReason = StopCriterion.MaxIterations;
        /// <summary>
        /// Счетчик итераций
        /// </summary>
        this.iterationsPerformed = 0;
    }
    return AlgorithmResult;
}());
exports.AlgorithmResult = AlgorithmResult;
